import { Button } from '@tark/shared/components';
import {
  useDownloadOrganizerCompetitionExport,
  useOrganizerCompetitionLaunchReadiness,
} from '@tark/shared/hooks';
import { getErrorMessage } from '@tark/shared/lib';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { useState } from 'react';

export default function OrganizerLaunchPanel({
  competitionPublicId,
  canAdmin,
}: {
  competitionPublicId: string;
  canAdmin: boolean;
}) {
  const readiness = useOrganizerCompetitionLaunchReadiness(competitionPublicId, true);
  const download = useDownloadOrganizerCompetitionExport(competitionPublicId);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const saveExport = async () => {
    setDownloadError(null);
    try {
      const file = await download.mutateAsync();
      const url = URL.createObjectURL(file.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(getErrorMessage(error));
    }
  };

  const data = readiness.data;
  const ready = data?.ready === true;
  const issueCount = data?.issues.length ?? 0;

  return (
    <section className="border-y border-border py-5" aria-labelledby="competition-launch-title">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${ready ? 'bg-emerald-500/10 text-emerald-700' : 'bg-primary/10 text-primary'}`}>
            {ready ? <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> : <ShieldCheck className="h-5 w-5" aria-hidden="true" />}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="competition-launch-title" className="text-lg font-semibold text-text">Launch check</h2>
              {data ? (
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${ready ? 'bg-emerald-500/10 text-emerald-700' : 'bg-amber-500/10 text-amber-700'}`}>
                  {ready ? 'Ready' : `${issueCount} to fix`}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-sm text-text-muted">
              {ready
                ? `${data.divisions.length} ${data.divisions.length === 1 ? 'division is' : 'divisions are'} ready for draw publication.`
                : 'Checks the field, scoring rules, capacity, dates, and timezone before publication.'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            leftIcon={readiness.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            disabled={readiness.isFetching}
            onClick={() => void readiness.refetch()}
          >
            Recheck
          </Button>
          {canAdmin ? (
            <Button
              size="sm"
              variant="outline"
              leftIcon={download.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              disabled={download.isPending}
              onClick={() => void saveExport()}
            >
              Export CSV
            </Button>
          ) : null}
        </div>
      </div>

      {readiness.isLoading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-text-muted" role="status">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Checking launch setup
        </div>
      ) : readiness.isError ? (
        <div className="mt-4 flex items-center gap-2 text-sm font-medium text-red-600" role="alert">
          <AlertCircle className="h-4 w-4" /> Launch setup could not be checked. Recheck before publishing.
        </div>
      ) : data && !data.ready ? (
        <ul className="mt-4 grid gap-2 sm:grid-cols-2" aria-label="Launch issues">
          {data.issues.map((issue) => (
            <li key={`${issue.code}-${issue.divisionPublicId ?? 'competition'}`} className="flex items-start gap-2 text-sm text-text-muted">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              <span>{issue.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {downloadError ? (
        <p className="mt-3 text-sm font-medium text-red-600" role="alert">{downloadError}</p>
      ) : null}
    </section>
  );
}
